/**
 * Tournament Event aggregate repository (ADR-031 P3).
 * Path: arenas/{arenaId}/events/{eventId}
 * Event owns competitive state; Arena does not embed managedEvents.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getDocsFromServer,
  onSnapshot,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";

function eventsCol(arenaId) {
  return collection(db, "arenas", arenaId, "events");
}

function eventRef(arenaId, eventId) {
  return doc(db, "arenas", arenaId, "events", eventId);
}

/**
 * @param {string} arenaId
 * @returns {Promise<object[]>}
 */
export async function listEvents(arenaId, opts = {}) {
  if (!arenaId) return [];
  const read = opts.fromServer ? getDocsFromServer : getDocs;
  let snap;
  try {
    snap = await read(eventsCol(arenaId));
  } catch (err) {
    if (!opts.fromServer) throw err;
    snap = await getDocs(eventsCol(arenaId));
  }
  return snap.docs
    .map((d) => ({ ...d.data(), eventId: d.id, arenaId }))
    .sort((a, b) => (a.createdAtMs || 0) - (b.createdAtMs || 0));
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 */
export async function fetchEvent(arenaId, eventId) {
  if (!arenaId || !eventId) return null;
  const snap = await getDoc(eventRef(arenaId, eventId));
  if (!snap.exists()) return null;
  return { ...snap.data(), eventId: snap.id, arenaId };
}

function stripUndefinedDeep(value) {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== "object") return value;
  // Preserve Firestore FieldValue / Timestamp / Date sentinels
  if (typeof value.toMillis === "function" || typeof value.isEqual === "function") {
    return value;
  }
  if (Object.prototype.toString.call(value) === "[object Date]") return value;
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefinedDeep(item)).filter((item) => item !== undefined);
  }
  /** @type {Record<string, unknown>} */
  const out = {};
  Object.keys(value).forEach((key) => {
    const next = stripUndefinedDeep(value[key]);
    if (next !== undefined) out[key] = next;
  });
  return out;
}

/**
 * @param {string} arenaId
 * @param {object} event
 */
export async function upsertEvent(arenaId, event) {
  const eventId = event.eventId;
  if (!arenaId || !eventId) throw new Error("arenaId and eventId required.");
  const now = Date.now();
  const payload = stripUndefinedDeep({
    ...event,
    eventId,
    arenaId,
    updatedAtMs: event.updatedAtMs ?? now,
    createdAtMs: event.createdAtMs ?? now,
    updatedAt: serverTimestamp(),
    createdAt: event.createdAt || serverTimestamp(),
  });
  await setDoc(eventRef(arenaId, eventId), payload, { merge: true });
  return { ...event, eventId, arenaId };
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 * @param {Record<string, unknown>} patch
 */
export async function updateEvent(arenaId, eventId, patch) {
  await updateDoc(eventRef(arenaId, eventId), {
    ...patch,
    updatedAtMs: Date.now(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Touch event so Arena tournament subscribers re-hydrate children.
 * @param {string} arenaId
 * @param {string} eventId
 */
export async function touchEvent(arenaId, eventId) {
  if (!arenaId || !eventId) return;
  try {
    await updateEvent(arenaId, eventId, { revisionMs: Date.now() });
  } catch {
    /* best-effort */
  }
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 */
export async function deleteEvent(arenaId, eventId) {
  if (!arenaId || !eventId) return;
  await deleteDoc(eventRef(arenaId, eventId));
}

/**
 * @param {string} arenaId
 * @param {(events: object[]) => void} onData
 */
export function subscribeEvents(arenaId, onData) {
  if (!arenaId) {
    onData?.([]);
    return () => {};
  }
  return onSnapshot(eventsCol(arenaId), (snap) => {
    const rows = snap.docs
      .map((d) => ({ ...d.data(), eventId: d.id, arenaId }))
      .sort((a, b) => (a.createdAtMs || 0) - (b.createdAtMs || 0));
    onData?.(rows);
  });
}
